import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { fetchWatomsSystemSurveyForm, insertWatomsManagersSurveyForm } from "services/watoms/watomsData";
import { useAuth } from "context/AuthContext";
import toast from "react-hot-toast";

const WatomsHelperPage = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [form, setForm] = useState([]);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    const loadSystemSurveyForm = async () => {
      const response = await fetchWatomsSystemSurveyForm();
      setForm(response || []);
    };
    loadSystemSurveyForm();
  }, []);

  const totalQuestions = useMemo(() => {
    return (form || []).reduce((acc, g) => acc + (g.questions?.length || 0), 0);
  }, [form]);

  const answeredCount = useMemo(() => Object.keys(selected).length, [selected]);

  const selectAnswer = (groupId, questionId, answerId) => {
    const key = `${groupId}-${questionId}`;
    setSelected((prev) => ({
      ...prev,
      [key]: { groupId, questionId, answerId },
    }));
  };

  const handleSubmit = async () => {
    const payload = {
      user_id: userInfo?.id,
      answers: Object.values(selected).map((x) => ({
        answer_id: x.answerId,
      })),
    };

    await insertWatomsManagersSurveyForm(payload);

    toast.success("تم ارسال الاستبيان بنجاح");
    navigate('/watoms');
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-100 text-slate-900 font-[Cairo,sans-serif]"
      dir="rtl"
    >
      {/* Header */}
      <div className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/watoms")}
              className="text-blue-600 hover:text-blue-400 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-2xl" />
            </button>

            <div>
              <h1 className="text-2xl font-bold text-blue-600">استبيان</h1>
              <p className="text-sm text-slate-500 mt-1">
                تم الإجابة على {answeredCount} من {totalQuestions}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!userInfo?.id || answeredCount !== totalQuestions}
            className={`px-5 py-2 rounded-xl font-semibold transition
              ${
                !userInfo?.id || answeredCount === 0
                  ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            title={!userInfo?.id ? "يجب تسجيل الدخول" : ""}
          >
            إرسال
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-10">
        {(form || []).map((group) => (
          <div
            key={group.id}
            className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden"
          >
            {/* Group Title */}
            <div className="px-6 py-4 bg-blue-50 border-b border-slate-200">
              <h2 className="text-lg font-bold text-blue-700">{group.name}</h2>
            </div>

            {/* Questions */}
            <div className="p-6 space-y-6">
              {(group.questions || []).map((q, qIdx) => {
                const qKey = `${group.id}-${q.id}`;
                const selectedAnswerId = selected[qKey]?.answerId;

                return (
                  <div key={qKey} className="rounded-2xl border border-slate-200 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-bold text-slate-800 leading-relaxed">
                        {qIdx + 1}. {q.name}
                      </p>

                      {selectedAnswerId ? (
                        <span className="text-green-600 shrink-0">
                          <FontAwesomeIcon icon={faCheckCircle} className="text-lg" />
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-4 space-y-2">
                      {(q.answers || []).map((ans) => {
                        const isSelected = selectedAnswerId === ans.id;

                        return (
                          <button
                            key={ans.id}
                            type="button"
                            onClick={() => selectAnswer(group.id, q.id, ans.id)}
                            className={`w-full text-right rounded-xl border px-4 py-3 transition flex items-center justify-between gap-4
                              ${
                                isSelected
                                  ? "border-blue-600 bg-blue-50"
                                  : "border-slate-200 bg-white hover:bg-slate-50"
                              }`}
                          >
                            <span className="text-slate-800">{ans.name}</span>

                            {/* radio dot */}
                            <span
                              className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0
                                ${
                                  isSelected
                                    ? "border-blue-600"
                                    : "border-slate-300"
                                }`}
                            >
                              {isSelected ? (
                                <span className="w-3 h-3 rounded-full bg-blue-600" />
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {(group.questions || []).length === 0 && (
                <div className="text-slate-500">لا توجد أسئلة في هذا القسم</div>
              )}
            </div>
          </div>
        ))}

        {(!form || form.length === 0) && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 px-6 py-6 text-slate-600">
            لا توجد بيانات لعرضها
          </div>
        )}
      </div>
    </div>
  );
};

export default WatomsHelperPage;